import { IDatePickerStrings } from "@fluentui/react";
import { DayOfWeek, PaymentTermType } from "../api-clients/api";
import i18n from "../i18n/i18n";

var numeral = require("numeral");

export const getCulture = () => {
  return i18n.language === "it" ? "it-IT" : "fr-FR";
};

export const numberToCurrency = (value: number, currency: string) => {
  return new Intl.NumberFormat(getCulture(), {
    style: "currency",
    currency: currency,
  }).format(value);
};

export const numberToMad = (value?: number) => {
  return new Intl.NumberFormat(getCulture(), {
    style: "currency",
    currency: "EUR",
  })
    .format(value ?? 0)
    .replace("EUR", "dh");
};

export const numberToMadPdf = (value?: number) => {
  return value === 0
    ? "-"
    : new Intl.NumberFormat(getCulture(), {
        style: "currency",
        currency: "EUR",
      })
        .format(value ?? 0)
        .replace("EUR", "dh")
        .replace(" ", " ");
};

export const numberCompacted = (value: number) => {
  return numeral(value).format("(0.0a)");
};

export const numberToShortMad = (value: number) => {
  return "EUR " + numeral(value).format("(0.0a)");
};

export const dateToString = (date?: Date): string => {
  if (date) return date.toLocaleDateString(getCulture());
  else return "";
};

export const paymentTermTypeToString = (
  paymentTermType?: PaymentTermType
): string => {
  switch (paymentTermType) {
    case +PaymentTermType._1:
      return i18n.t("helper.paymentTerm.exchangeLetter");
    case +PaymentTermType._2:
      return i18n.t("helper.paymentTerm.cash");
    case +PaymentTermType._3:
      return i18n.t("helper.paymentTerm.transfer");
    case +PaymentTermType._4:
      return i18n.t("helper.paymentTerm.deposit");
    case +PaymentTermType._5:
      return i18n.t("helper.paymentTerm.check");
    case +PaymentTermType._6:
      return i18n.t("helper.paymentTerm.other");

    default:
      return "";
  }
};

export interface HeaderDetail {
  name: string;
  label: string;
  formatToMoney: boolean;
  currency: string;
}

export const monthName = (month: number) => {
  const months = i18n.t("datePicker.months", {
    returnObjects: true,
  }) as unknown as string[];
  return months[month - 1] ?? month.toString();
};

// ClassSchedule start/end times are plain "HH:mm:ss" strings on the wire (the backend
// serializes/parses .NET TimeSpan as that string via a custom JSON converter — sending or
// expecting anything else, e.g. an object of {hours, minutes, ...}, silently loses the time).
export const timeSpanToDate = (ts?: string | null): Date => {
  const date = new Date(2000, 0, 1);
  const [hours, minutes] = (ts ?? "00:00:00").split(":").map(Number);
  date.setHours(hours || 0, minutes || 0, 0, 0);
  return date;
};

export const dateToTimeSpan = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}:00`;
};

export const timeSpanToString = (ts?: string | null): string => {
  if (ts == null || ts.length === 0) return "";
  const [hours, minutes] = ts.split(":");
  return `${hours ?? "00"}:${minutes ?? "00"}`;
};

export const dayOfWeekToString = (day?: DayOfWeek): string => {
  const days = i18n.t("datePicker.days", {
    returnObjects: true,
  }) as unknown as string[];
  return day != null ? days[+day] ?? "" : "";
};

export const daysOfWeekOrdered: DayOfWeek[] = [
  DayOfWeek._1,
  DayOfWeek._2,
  DayOfWeek._3,
  DayOfWeek._4,
  DayOfWeek._5,
  DayOfWeek._6,
  DayOfWeek._0,
];

export const getDayPickerStrings = (): IDatePickerStrings => ({
  months: i18n.t("datePicker.months", {
    returnObjects: true,
  }) as unknown as string[],

  shortMonths: i18n.t("datePicker.shortMonths", {
    returnObjects: true,
  }) as unknown as string[],

  days: i18n.t("datePicker.days", {
    returnObjects: true,
  }) as unknown as string[],

  shortDays: i18n.t("datePicker.shortDays", {
    returnObjects: true,
  }) as unknown as string[],

  goToToday: i18n.t("datePicker.goToToday"),
  prevMonthAriaLabel: i18n.t("datePicker.prevMonthAriaLabel"),
  nextMonthAriaLabel: i18n.t("datePicker.nextMonthAriaLabel"),
  prevYearAriaLabel: i18n.t("datePicker.prevYearAriaLabel"),
  nextYearAriaLabel: i18n.t("datePicker.nextYearAriaLabel"),
  closeButtonAriaLabel: i18n.t("common.close"),
  monthPickerHeaderAriaLabel: i18n.t("datePicker.monthPickerHeaderAriaLabel"),
  yearPickerHeaderAriaLabel: i18n.t("datePicker.yearPickerHeaderAriaLabel"),

  isRequiredErrorMessage: i18n.t("datePicker.isRequiredErrorMessage"),

  invalidInputErrorMessage: i18n.t("datePicker.invalidInputErrorMessage"),
});
